'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronRightIcon } from '@/components/Icons/Icons';
interface DropdownOption {
  value: string | number;
  label: string;
  href?: string;
}

interface DropdownProps {
  options?: DropdownOption[];
  defaultValue?: string | number;
  eyebrowText?: string;
  placeholder?: string;
  onChange?: (value: string | number) => void;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  icon?: React.ReactNode;
  id?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  defaultValue,
  eyebrowText,
  placeholder = 'Select an option',
  onChange,
  onOpenChange,
  className = '',
  icon = <ChevronRightIcon size={20} className="rotate-90" />,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  
  // Notify parent when open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);
  const [selectedValue, setSelectedValue] = useState<string | number | undefined>(defaultValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sync selectedValue with defaultValue when it changes
  useEffect(() => {
    if (defaultValue !== undefined) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  // Get the label for the selected value
  const selectedOption = options?.find((opt) => opt.value === selectedValue);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // Check if dropdown should open upward and scroll into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && menuRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const menuHeight = 240; // max-h-60 = 240px
      const isMobileOrTablet = window.innerWidth < 1024;
      
      // On mobile/tablet: open upward if there's enough space above, otherwise open downward
      // On desktop: always open downward unless there's truly not enough space below
      if (isMobileOrTablet) {
        // Mobile/tablet: prefer opening upward if space is available
          setOpenUpward(false);
      } else {
        // Desktop: always open downward unless there's insufficient space below (less than 100px)
        // and more space above
        if (spaceBelow < 100 && spaceAbove > spaceBelow) {
          setOpenUpward(true);
        } else {
          setOpenUpward(false);
        }
      }
      
      // Scroll dropdown into view on mobile
      if (isMobileOrTablet) {
        setTimeout(() => {
          dropdownRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle option selection
  const handleSelect = (value: string | number) => {
    setSelectedValue(value);
    setIsOpen(false);
    if (onChange) {
      onChange(value);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="w-inherit relative inline-block"
      onKeyDown={handleKeyDown}
    >
      {/* Dropdown Button */} 
      <div
        id={id}
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        tabIndex={0}
        className={`cs-input-field relative flex min-h-[40px] w-full items-center justify-between px-3 py-2.5 transition-colors outline-none hover:bg-surface-4 focus-visible:ring-2 focus-visible:ring-[var(--Color-Extended-Amethyst-Amethyst-Accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:rounded-none ${isOpen ? 'ring-2 ring-[var(--Color-Extended-Amethyst-Amethyst-Accent)] ring-offset-2 ring-offset-background rounded-none' : ''} ${className}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex items-center truncate text-left cs-input-field text-text-default-medium">
          {eyebrowText && <span className="cs-s-body mr-1 font-medium">{eyebrowText}</span>}
          {displayText}
        </span>
        <span
          className={`flex text-icon-strong items-center transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
        >
          {icon}
        </span>
      </div>

      {/* Dropdown Menu */}
      <div
        ref={menuRef}
        className={`absolute left-0 w-full transition-all duration-300 ease-in-out ${
          openUpward 
            ? 'bottom-full mb-1' 
            : 'top-full mt-1'
        } ${
          isOpen ? 'max-h-60 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        }`}
      >
        <ul
          role="listbox"
          className="bg-surface-3 cs-s-body relative z-50 w-full overflow-auto rounded-lg shadow-lg cursor-pointer"
        >
          {options && options.length > 0 ? (
            options.map((option, index) => {
              const isLast = index === options.length - 1;
              const isSelected = selectedValue === option.value;
              
              return (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    handleSelect(option.value);
                    if (option.href) {
                      const rawHref = String(option.href).trim();
                      const isAbsolute = rawHref.startsWith('http://') || rawHref.startsWith('https://');
                      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';
                      const resolvedHref = isAbsolute
                        ? rawHref
                        : rawHref.startsWith('/')
                          ? `${basePath}${rawHref}`
                          : rawHref.startsWith('product/')
                            ? `${basePath}/${rawHref}`
                            : `${basePath}/product/${rawHref}`;
                      if (isAbsolute) {
                        window.open(rawHref, '_blank', 'noopener,noreferrer');
                      } else {
                        window.location.href = resolvedHref;
                      }
                    }
                  }}
                  className={`relative cursor-pointer px-3 py-[10px] text-left transition-all ${
                    !isLast && !isSelected
                      ? 'after:absolute after:right-3 after:bottom-0 after:left-3 after:block after:h-px after:bg-border-medium after:content-[""]'
                      : ''
                  } ${
                    isSelected
                      ? 'cs-s-body font-medium ring-2 ring-[var(--Color-Extended-Amethyst-Amethyst-Accent)] ring-inset text-text-default-strong'
                      : 'cs-s-body hover:ring-2 hover:ring-[var(--Color-Extended-Amethyst-Amethyst-Accent)] hover:ring-inset'
                  } first:rounded-t-lg last:rounded-b-lg`}
                >
                  <span className="block">
                    {option.label}
                  </span>
                </li>
              );
            })
          ) : (
            <li className="text-text-default-subtle px-4 py-2.5 text-center">
              No options available
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Dropdown;
